import { useRef, useState } from 'react'

import { describeCode, describeError, isIpPermissionDenied } from '../../lib/api/messages.ts'
import { companies } from '../../lib/api/resources.ts'
import { RELEASE_TTL_DEFAULT_SECONDS } from '../../lib/api/types.ts'
import type { FleetIpCommandResponse } from '../../lib/api/types.ts'
import { cidrProblem, ipProblem } from '../../lib/ip/semantics.ts'
import { useAction } from '../../lib/useAction.ts'
import { Button } from '../primitives/Button.tsx'
import { Dialog } from '../primitives/Dialog.tsx'
import { SelectField, TextField } from '../primitives/Field.tsx'
import { ShortId } from '../primitives/CopyValue.tsx'
import { Notice } from '../primitives/States.tsx'
import { TABLE, TD, TH, THEAD, TR } from '../primitives/table.ts'
import { TTL_OPTIONS } from './ReleaseHost.tsx'

/**
 * Resultado de uma operação de frota. É POR MÁQUINA: `failed > 0` não é erro da
 * operação inteira — as `queued` seguem a vida, e as demais ficaram de fora com
 * um `error_code` cada. Cada comando enfileirado se acompanha na sua máquina.
 */
export function FleetResult({ result }: { result: FleetIpCommandResponse }) {
  const left = result.items.filter((item) => item.command === null)
  return (
    <div className="space-y-3">
      <Notice tone={result.failed > 0 ? 'warn' : 'neutral'}>
        <span className="font-mono text-fg">{result.target}</span>:{' '}
        <strong>{result.queued}</strong> máquina(s) na fila, <strong>{result.failed}</strong> de fora.
      </Notice>

      {left.length > 0 && (
        <table className={TABLE}>
          <thead className={THEAD}>
            <tr>
              <th scope="col" className={TH}>Máquina</th>
              <th scope="col" className={TH}>Código</th>
              <th scope="col" className={TH}>Motivo</th>
            </tr>
          </thead>
          <tbody>
            {left.map((item) => (
              <tr key={item.endpoint_id} className={TR}>
                <td className={`${TD} whitespace-nowrap`}>
                  <ShortId id={item.endpoint_id} label="endpoint_id" />
                </td>
                <td className={`${TD} font-mono text-micro`}>{item.error_code ?? '—'}</td>
                <td className={TD}>{describeCode(item.error_code, item.error_message)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

/** Bloquear ou permitir um IP em TODAS as máquinas da empresa (um comando por máquina). */
export function FleetIpDialog({
  companyId,
  companyName,
  mode,
  target = '',
  onClose,
}: {
  companyId: string
  companyName: string
  mode: 'block' | 'allow'
  target?: string
  onClose: () => void
}) {
  const action = useAction()
  const block = mode === 'block'
  const [value, setValue] = useState(target)
  const [text, setText] = useState('')
  const [ttl, setTtl] = useState(String(RELEASE_TTL_DEFAULT_SECONDS))
  const [result, setResult] = useState<FleetIpCommandResponse | null>(null)
  const submitted = useRef(false)

  const problem = block ? ipProblem(value) : cidrProblem(value)
  const denied = isIpPermissionDenied(action.error)
  const disabled = denied || value.trim() === '' || problem !== null || text.trim() === '' || result !== null

  const submit = (): void => {
    if (disabled || submitted.current) return
    action.run(async () => {
      const expiresInSeconds = Number(ttl)
      const done = block
        ? await companies.blockIp(companyId, { ip: value.trim(), reason: text.trim(), expiresInSeconds })
        : await companies.allowIp(companyId, { cidr: value.trim(), note: text.trim(), expiresInSeconds })
      setResult(done)
    })
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={block ? 'Bloquear IP em toda a empresa' : 'Permitir IP/rede em toda a empresa'}
      description={<span className="font-medium text-fg">{companyName}</span>}
      wide
    >
      {result !== null ? (
        <div className="space-y-3">
          <FleetResult result={result} />
          <div className="flex justify-end border-t border-line-subtle pt-3">
            <Button variant="primary" onClick={onClose}>
              Fechar
            </Button>
          </div>
        </div>
      ) : (
        <form
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault()
            submit()
          }}
        >
          {!block && (
            <Notice tone="warn">Libera também o que estiver bloqueado dentro da rede, em todas as máquinas.</Notice>
          )}
          <TextField
            label={block ? 'IP a bloquear' : 'IP ou rede (CIDR)'}
            value={value}
            onValueChange={setValue}
            error={problem}
            required
            mono
            autoFocus
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            hint={block ? undefined : 'ex.: 203.0.113.0/24'}
          />
          <TextField
            label={block ? 'Motivo' : 'Nota'}
            value={text}
            onValueChange={setText}
            maxLength={512}
            required
          />
          <SelectField
            label="Validade do pedido"
            value={ttl}
            onValueChange={setTtl}
            options={TTL_OPTIONS}
            className="max-w-60"
          />
          <div className="flex items-center justify-end gap-2 border-t border-line-subtle pt-3">
            {action.error !== null && (
              <p role="alert" className="mr-auto text-sm leading-snug text-crit">
                {describeError(action.error)}
              </p>
            )}
            <Button variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" variant={block ? 'primary' : 'danger'} busy={action.busy} disabled={disabled}>
              {block ? 'Bloquear na frota' : 'Permitir na frota'}
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  )
}
